/**
 * GuestJoinPage - Page for guests to join a collaboration space via public link
 * Features: Name input (optional), Avatar selection, Join button
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Loader2, Link2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useSafeMode } from '../contexts/SafeModeContext';
import { collaborationApi } from '../services/api';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

// Avatar grid positions (9x4 = 36 avatars from the animal avatar sheet)
const AVATARS = [
  // Row 1
  { id: 1, name: 'Lemur', row: 0, col: 0 },
  { id: 2, name: 'Fox', row: 0, col: 1 },
  { id: 3, name: 'Zebra', row: 0, col: 2 },
  { id: 4, name: 'Deer', row: 0, col: 3 },
  { id: 5, name: 'Fox Lady', row: 0, col: 4 },
  { id: 6, name: 'Bird', row: 0, col: 5 },
  { id: 7, name: 'Professor', row: 0, col: 6 },
  { id: 8, name: 'Bear', row: 0, col: 7 },
  { id: 9, name: 'Cat', row: 0, col: 8 },
  // Row 2
  { id: 10, name: 'Tiger', row: 1, col: 0 },
  { id: 11, name: 'Gazelle', row: 1, col: 1 },
  { id: 12, name: 'Beaver', row: 1, col: 2 },
  { id: 13, name: 'Giraffe', row: 1, col: 3 },
  { id: 14, name: 'Flamingo', row: 1, col: 4 },
  { id: 15, name: 'Wolf', row: 1, col: 5 },
  { id: 16, name: 'Llama', row: 1, col: 6 },
  { id: 17, name: 'Koala', row: 1, col: 7 },
  { id: 18, name: 'Bear Fancy', row: 1, col: 8 },
  // Row 3
  { id: 19, name: 'Tiger Cute', row: 2, col: 0 },
  { id: 20, name: 'Alpaca', row: 2, col: 1 },
  { id: 21, name: 'Bunny', row: 2, col: 2 },
  { id: 22, name: 'Lady', row: 2, col: 3 },
  { id: 23, name: 'Panda', row: 2, col: 4 },
  { id: 24, name: 'Donkey', row: 2, col: 5 },
  { id: 25, name: 'Pig', row: 2, col: 6 },
  { id: 26, name: 'Fox Fancy', row: 2, col: 7 },
  { id: 27, name: 'Cat Lady', row: 2, col: 8 },
  // Row 4
  { id: 28, name: 'Corgi', row: 3, col: 0 },
  { id: 29, name: 'Dog', row: 3, col: 1 },
  { id: 30, name: 'Otter', row: 3, col: 2 },
  { id: 31, name: 'Frog', row: 3, col: 3 },
  { id: 32, name: 'Platypus', row: 3, col: 4 },
  { id: 33, name: 'Dragon', row: 3, col: 5 },
  { id: 34, name: 'Sloth', row: 3, col: 6 },
  { id: 35, name: 'Koala Bow', row: 3, col: 7 },
  { id: 36, name: 'Owl', row: 3, col: 8 },
];

// Avatar image URL (9 columns x 4 rows grid)
const AVATAR_SHEET_URL = 'https://customer-assets.emergentagent.com/job_b353f328-c785-48ef-8d8b-6ae791a336a7/artifacts/99xqh56e_set-of-funny-animal-portraits-vector.jpg';

const AvatarSelector = ({ selectedAvatar, onSelect }) => {
  return (
    <div className="flex justify-center">
      <div className="grid grid-cols-6 gap-2 max-h-64 overflow-y-auto p-1">
        {AVATARS.map(avatar => (
          <button
            key={avatar.id}
            onClick={() => onSelect(avatar.id)}
            className={cn(
              "relative w-14 h-14 rounded-lg overflow-hidden border-2 transition-all hover:scale-105",
              selectedAvatar === avatar.id
                ? "border-blue-500 ring-2 ring-blue-300"
                : "border-gray-200 hover:border-gray-300"
          )}
          title={avatar.name}
        >
          <div
            className="w-full h-full bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url(${AVATAR_SHEET_URL})`,
              backgroundSize: '900% 400%',
              backgroundPosition: `${avatar.col * 12.5}% ${avatar.row * 33.33}%`,
            }}
          />
          {selectedAvatar === avatar.id && (
            <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-blue-600" />
            </div>
          )}
        </button>
        ))}
      </div>
    </div>
  );
};

const GuestJoinPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();

  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [spaceInfo, setSpaceInfo] = useState(null);
  const [name, setName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(1);

  // Load space information
  useEffect(() => {
    const loadSpaceInfo = async () => {
      try {
        setLoading(true);
        const response = await collaborationApi.getPublicSpaceInfo(token);
        setSpaceInfo(response.data);
      } catch (err) {
        console.error('Failed to load space info:', err);
        setError(err.response?.data?.detail || 'This link is invalid or sharing has been disabled');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadSpaceInfo();
    }
  }, [token]);

  // Join as guest
  const handleJoin = async () => {
    setJoining(true);
    try {
      const response = await collaborationApi.joinAsGuest(token, {
        name: name.trim() || null,
        avatar_id: selectedAvatar,
      });
      
      // Store guest session in localStorage
      localStorage.setItem('guestSession', JSON.stringify({
        token: response.data.guest_session_token,
        guestId: response.data.guest_id,
        name: response.data.name,
        avatarId: response.data.avatar_id,
        spaceId: response.data.space_id,
        role: response.data.role,
      }));
      
      toast.success('Welcome! Joining the collaboration space...');
      
      // Navigate to the collaboration canvas
      window.location.href = `/collaboration/${response.data.space_id}?guest=${response.data.guest_session_token}`;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to join the space');
      toast.error('Failed to join');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center", colors.bgPrimary)}>
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center p-4", colors.bgPrimary)}>
        <Card className={cn("p-8 max-w-md text-center", colors.bgCard, colors.borderPrimary)}>
          <Link2 className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h1 className={cn("text-xl font-semibold mb-2", colors.textPrimary)}>
            Link Not Available
          </h1>
          <p className={cn("mb-6", colors.textSecondary)}>
            {error}
          </p>
          <Button onClick={() => navigate('/')} variant="outline">
            Go to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen flex items-center justify-center p-4", colors.bgPrimary)}>
      <Card className={cn("p-8 max-w-lg w-full", colors.bgCard, colors.borderPrimary)}>
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-4">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className={cn("text-2xl font-bold mb-1", colors.textPrimary)}>
            Join Collaboration
          </h1>
          <p className={cn("text-lg font-medium", colors.textAccent)}>
            {spaceInfo?.name}
          </p>
          <p className={cn("text-sm mt-1", colors.textMuted)}>
            Shared by {spaceInfo?.owner_name}
          </p>
          <div className={cn(
            "inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs",
            spaceInfo?.permission === 'edit' ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
          )}>
            {spaceInfo?.permission === 'edit' ? 'Can edit' : 'Can view'}
          </div>
        </div>

        <div className="space-y-6">
          {/* Name Input */}
          <div>
            <Label htmlFor="name" className={colors.textSecondary}>
              Your Name (optional)
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name or leave blank"
              className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
            />
            <p className={cn("text-xs mt-1", colors.textMuted)}>
              If left blank, you&apos;ll appear as &quot;Person 1&quot;, &quot;Person 2&quot;, etc.
            </p>
          </div>

          {/* Avatar Selection */}
          <div>
            <Label className={cn("mb-2 block", colors.textSecondary)}>
              Choose Your Avatar
            </Label>
            <AvatarSelector
              selectedAvatar={selectedAvatar}
              onSelect={setSelectedAvatar}
            />
          </div>

          {/* Join Button */}
          <Button
            onClick={handleJoin}
            disabled={joining}
            className="w-full"
            size="lg"
          >
            {joining ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Joining...
              </>
            ) : (
              <>
                <Users className="w-4 h-4 mr-2" />
                Join Space
              </>
            )}
          </Button>

          <p className={cn("text-xs text-center", colors.textMuted)}>
            By joining, you can collaborate in this space until the owner removes your access.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default GuestJoinPage;
